import { useQuery } from '@tanstack/react-query'
import { memo, useMemo, useState } from 'react'

import { rulesQuery } from '@/api/actions'

import { eventLabel } from '../describe'
import { EnabledField, type FormProps, type Update } from './ActionForm'
import { useEditor } from './context'
import { asList, asObj, asStrings, deleteIn, isObj, setIn, setOrDelete, slugify, uniqueName } from './doc'
import { CheckLine, EditField, IssueLines, NumberInput, SectionCard, SelectInput, TagInput, TextInput } from './fields'
import { ResultsEditor } from './ListsEditors'
import { KeyValueRows, RoleSpecFields } from './RoleSpecFields'
import { StepsEditor } from './StepsEditor'

/**
 * The world-rule form (§11.6): when an event happens between "the thing" (`subject`) and "the
 * other thing" (`other`), the world does some steps and changes some state. Same kit as the action
 * form; the steps and results speak of `$subject` and `$other` (the editor puts exactly those two
 * in scope).
 */

const EVENT_HELP: Record<string, string> = {
  land: 'something lands on or in something (after being thrown or dropped)',
  enter: 'someone or something goes into a place (walks into water, through a door)',
  leave: 'someone or something leaves a place',
  contact: 'two things touch (something is caught or picked up)',
  drop: 'something is let go',
  arrive: 'someone or something arrives where it was going',
  state_change: 'something changes (gets wet, opens, falls asleep…)',
}

export function RuleForm({ doc, update, mode, takenIds }: FormProps) {
  const on = useMemo(
    () => ({
      then: (v: unknown[]) => update((d) => setIn(d, ['then'], v)),
      result: (v: unknown[]) => update((d) => setIn(d, ['result'], v)),
    }),
    [update],
  )
  return (
    <div className="flex flex-col gap-6">
      <RuleBasics
        update={update}
        mode={mode}
        takenIds={takenIds}
        label={doc.label}
        id={doc.id}
        description={doc.description}
        enabled={doc.enabled !== false}
        priority={doc.priority}
        once={doc.once_per_pair !== false}
      />
      <WhenSection when={doc.when} update={update} />
      <ThenSection value={doc.then} onChange={on.then} />
      <RuleResultSection value={doc.result} onChange={on.result} />
      <SupersedesSection value={doc.supersedes} selfId={typeof doc.id === 'string' ? doc.id : ''} update={update} />
    </div>
  )
}

interface RuleBasicsProps {
  update: Update
  mode: 'edit' | 'new'
  takenIds: string[]
  label: unknown
  id: unknown
  description: unknown
  enabled: boolean
  priority: unknown
  once: boolean
}

const RuleBasics = memo(function RuleBasics({ update, mode, takenIds, label, id, description, enabled, priority, once }: RuleBasicsProps) {
  const [idByHand, setIdByHand] = useState(false)
  const enabledField = <EnabledField checked={enabled} onChange={(v) => update((d) => setIn(d, ['enabled'], v))} />
  return (
    <SectionCard path="" title="Basics" help="What it is called and whether new stories follow it." testId="section-basics">
      {/* The id can't change once saved; a problem with it (changed in the JSON) shows here. */}
      {mode === 'edit' ? <IssueLines path="id" /> : null}
      <div className="grid gap-4 md:grid-cols-2">
        <EditField path="label" label="Name" hint="Short, like “Splash in water”.">
          {(ctrl) => (
            <TextInput
              ctrl={ctrl}
              value={label}
              placeholder="Splash in water"
              onChange={(v) =>
                update((d) => {
                  const next = setIn(d, ['label'], v)
                  return mode === 'new' && !idByHand ? setIn(next, ['id'], uniqueName(slugify(v), takenIds)) : next
                })
              }
            />
          )}
        </EditField>
        {mode === 'new' ? (
          <EditField path="id" label="Saved as" hint="Letters, numbers and _ — made from the name. It can’t change later.">
            {(ctrl) => (
              <TextInput
                ctrl={ctrl}
                mono
                value={id}
                onChange={(v) => {
                  setIdByHand(true)
                  update((d) => setIn(d, ['id'], v.toLowerCase().replace(/\s+/g, '_')))
                }}
              />
            )}
          </EditField>
        ) : (
          enabledField
        )}
        <EditField path="description" label="In one line" hint="What the world does — the AI reads this too." className="md:col-span-2">
          {(ctrl) => <TextInput ctrl={ctrl} value={description} placeholder="anything that lands in water splashes" onChange={(v) => update((d) => setIn(d, ['description'], v))} />}
        </EditField>
        {mode === 'new' ? enabledField : null}
        <EditField path="priority" label="Priority (0–100)" hint="Higher goes first when several rules answer the same moment.">
          {(ctrl) => <NumberInput ctrl={ctrl} value={priority} min={0} max={100} step={1} placeholder="50" onChange={(v) => update((d) => setOrDelete(d, ['priority'], v))} />}
        </EditField>
        <CheckLine path="once_per_pair" label="Only once for each pair" hint="The same two things set it off one time only." checked={once} onChange={(v) => update((d) => setIn(d, ['once_per_pair'], v))} />
      </div>
    </SectionCard>
  )
})

const WhenSection = memo(function WhenSection({ when: raw, update }: { when: unknown; update: Update }) {
  const { meta } = useEditor()
  const when = asObj(raw)
  const hasOther = isObj(when.other)
  const event = typeof when.event === 'string' ? when.event : 'land'
  return (
    <SectionCard path="when" title="When" help="The moment that sets it off, and what kind of things it happens between." testId="section-when">
      <EditField path="when.event" label="What happens" hint={EVENT_HELP[event]}>
        {(ctrl) => <SelectInput ctrl={ctrl} value={event} options={meta.events.map((e) => ({ value: e, label: eventLabel(e) }))} onChange={(v) => update((d) => setIn(d, ['when', 'event'], v ?? 'land'))} />}
      </EditField>
      <div className="flex flex-col gap-3 rounded-2xl border border-line p-4" data-field-path="when.subject">
        <p className="text-sm font-semibold text-ink">The thing ($subject)</p>
        <RoleSpecFields path="when.subject" spec={asObj(when.subject)} who="the thing" onChange={(v) => update((d) => setIn(d, ['when', 'subject'], v))} />
      </div>
      <div className="flex flex-col gap-3 rounded-2xl border border-line p-4" data-field-path="when.other">
        <CheckLine
          path="when.other"
          deep={false}
          label="It happens with something else ($other)"
          hint="Off: only the thing itself matters (a state change, a drop)."
          checked={hasOther}
          onChange={(v) => update((d) => (v ? setIn(d, ['when', 'other'], { types: ['feature'] }) : deleteIn(d, ['when', 'other'])))}
        />
        {hasOther ? <RoleSpecFields path="when.other" spec={asObj(when.other)} who="the other thing" onChange={(v) => update((d) => setIn(d, ['when', 'other'], v))} /> : null}
      </div>
      <KeyValueRows
        path="when.state"
        label={event === 'state_change' ? 'What changes (and to what)' : 'The thing must be'}
        hint={event === 'state_change' ? 'For example: wet = true, or open = * for any change.' : 'Optional, for example: wet = false.'}
        value={asObj(when.state)}
        onChange={(v) => update((d) => (Object.keys(v).length ? setIn(d, ['when', 'state'], v) : deleteIn(d, ['when', 'state'])))}
      />
    </SectionCard>
  )
})

interface ListSectionProps {
  value: unknown
  onChange(v: unknown[]): void
}

const ThenSection = memo(function ThenSection({ value, onChange }: ListSectionProps) {
  return (
    <SectionCard path="then" title="Then" help="What the world does, step by step. $subject is the thing, $other the other thing." testId="section-then">
      <StepsEditor path="then" steps={asList(value)} onChange={onChange} />
    </SectionCard>
  )
})

const RuleResultSection = memo(function RuleResultSection({ value, onChange }: ListSectionProps) {
  return (
    <SectionCard path="result" title="Afterwards" help="What is true once it has happened." testId="section-result">
      <ResultsEditor path="result" items={asList(value)} onChange={onChange} />
    </SectionCard>
  )
})

const SupersedesSection = memo(function SupersedesSection({ value, selfId, update }: { value: unknown; selfId: string; update: Update }) {
  const rules = useQuery(rulesQuery({}))
  const ruleIds = (rules.data ?? []).map((r) => r.id).filter((id) => id !== selfId)
  return (
    <SectionCard path="supersedes" title="Replaces other rules" help="When both would answer the same moment, only this one does. Usually empty." testId="section-supersedes">
      <EditField path="supersedes" label="Rules it replaces">
        {(ctrl) => <TagInput ctrl={ctrl} values={asStrings(value)} suggestions={ruleIds} onChange={(v) => update((d) => setOrDelete(d, ['supersedes'], v))} placeholder="None" />}
      </EditField>
    </SectionCard>
  )
})
